import { Component, HostBinding, OnInit, QueryList, ViewChildren } from '@angular/core';
import { RowNode, SelectionChangedEvent } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DomainList } from '../../../../models/domain-lists';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, EntryType, HelpIndex, UpdateStatusType, SettingsType } from '../../../../models/enums';
import { CellTemplateType, FilterTemplateType, GenericColDef, GridContext, GridRowSelectionType } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions, PermissionSet } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { AddAliasesDomainEntriesModalComponent } from '../../../modals/add-aliases-domain-entries-modal/add-aliases-domain-entries-modal.component';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import * as _ from 'underscore';
import { DOMAINS_MAX_DISPLAY_GRID_ENTRIES, SYS_GENERATED_DOMAIN_LIST } from '../../../../models/constants';
import { AddSelectedTextToModalComponent, AddSelectedTextToModalData, AddSelectedTextToModalType } from '../../add-selected-text-to-modal/add-selected-text-to-modal.component';

@Component({
    standalone: false,
    selector: 'res-domain-lists',
    templateUrl: './domain-lists.component.html',
    styleUrls: ['./domain-lists.component.scss']
})

export class DomainListsComponent implements SettingsNavigationContentComponent, OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;

    isDirty: boolean;
    validChanges = true;
    currentHandle = '';
    helpIndex: any;
    selectedDomains = [];
    numberOfSelectedDomains: number;
    totalNumberOfDomainEntries: number;
    numOfDomainLists: number;
    domainGridEntriesCount: string;
    allDomainEntries = [];
    currentDomainEntries = [];
    domains = [];
    filterQuery = '';
    updateStatusType = UpdateStatusType.DOMAIN_MEMBERSHIP;

    gridName = 'Domain Lists Grid';
    automationId = 'domain-lists';
    domainEntriesAutoId = 'domain-entries';
    isBusy = false;
    isBusyMessage = 'Loading Domain Lists ...';
    selectedDomainList: DomainList;

    gridContext = new GridContext({
        inlineGridFilter: true,
        onFilterApply: (queryStr: string, filterType: FilterTemplateType = FilterTemplateType.freeform, filterColumnName: string = '') => this.onFilterApply(queryStr, filterType, filterColumnName),
    });

    hasPermissions = true;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Domain List Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            colId: 'context-menu',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    colDefsDomains = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            automationId: this.automationId,
            minWidth: 36, maxWidth: 36, width: 36,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            suppressSizeToFit: true,
        },
        {
            headerName: 'Domain Name',
            field: 'domainName',
            automationId: this.automationId,
            filterType: FilterTemplateType.freeform,
            suppressMovable: true
        }
    );

    domainListSelection = GridRowSelectionType.single;

    domainsSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_listDomains,
        responseName: 'domains',
    });
    needsBackendFiltering: boolean;

    constructor(
        private commonService: CommonService,
        private restService: RestService
    ) {
    }

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            enabled: (item) => this.isUserCreatedDomainListItem(item),
            click: (item) => this.editDomainList(item),
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            enabled: (item) => this.isUserCreatedDomainListItem(item),
            click: (item) => this.deleteDomainList(item)
        })
    ];

    rowDisableRulesDomains = {
        'disabled-row': () => {
            return !this.hasPermissions;
        },
    };

    isSystemUser = false;

    ngOnInit() {
        if (!this.hasPermissions) {
            // remove from grids checkbox and context menu columns for users with only VIEW rights
            this.colDefsDomains = this.colDefsDomains ? this.colDefsDomains.filter(col => col.colId !== 'checkbox') : this.colDefsDomains;
            this.columnDefs = this.columnDefs ? this.columnDefs.filter(col => col.colId !== 'context-menu') : this.columnDefs;
            this.contextMenuOptions = null;
        }
        if (this.isSystemUser) {
            Object.assign(this.requestInfo.data, { systemScope: this.isSystemUser });
        }
    }

    isUserCreatedDomainListItem(item: any): boolean {
        if (item?.name !== SYS_GENERATED_DOMAIN_LIST) {
            return true;
        }
        return false;
    }
    editDomainList(item: DomainList): void {
        const domainList = new DomainList(item);
        Object.assign(domainList, { isSystemUser: this.isSystemUser });
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: domainList }).then((response) => {
            if (response && response.modalData && response.modalData.domain) {
                this.selectedDomainList = response.modalData.domain;
            }
        });
    }

    deleteDomainList(item: DomainList): void {
        const domainList = new DomainList(item);
        Object.assign(domainList, { isSystemUser: this.isSystemUser });
        this.commonService.openModal(DeleteEntityModalComponent, {
            modalData: domainList
        }).then(() => {
            this.selectedDomainList = null;
        });
    }

    addNewDomainList(): void {
        const newDomainList = new DomainList({ projectHandle: this.currentHandle });
        Object.assign(newDomainList, { isSystemUser: this.isSystemUser });
        this.commonService.openModal((CreateEditEntityModalComponent), {
            modalData: newDomainList
        }).then((response) => {
            if (response && response.modalData && response.modalData.domain) {
                this.selectedDomainList = response.modalData.domain;
            }
        });
    }

    addDomains(): void {
        this.commonService.openModal(AddAliasesDomainEntriesModalComponent, {
            backdrop: false,
            modalData: {
                entryType: EntryType.domainEntries,
                listSelected: this.selectedDomainList,
                allDomainEntries: this.currentDomainEntries,
                systemScope: this.isSystemUser
            },
            class: 'horizontal-px-500'
        }).then(response => {
            if (response && response.modalData && response.modalData.domain && response.modalData.domain.handle && response.modalData.domain.projectHandle) {
                this.listDomainEntries(response.modalData.domain.handle, response.modalData.domain.projectHandle);
            }
        });
    }

    sysDomainIsSelected(): boolean {
        return this.selectedDomainList?.name === SYS_GENERATED_DOMAIN_LIST;
    }

    openDomainButton(isAddToExisting: boolean = false) {
        if (this.genericGrids?.last?.selectedRows?.length > 0 && this.currentHandle) {
            const selectedDomains = this.genericGrids.last.selectedRows.map(item => item.domainName);
            if (selectedDomains?.length > 0) {
                const newDomainList = new DomainList({ projectHandle: this.currentHandle, domainEntries: selectedDomains });
                Object.assign(newDomainList, { isSystemUser: this.commonService.currentUser.isSuperSystemUser });
                if (isAddToExisting) {
                    this.commonService.openModal((AddSelectedTextToModalComponent), {
                        modalData: <AddSelectedTextToModalData>{
                            type: AddSelectedTextToModalType.DomainList,
                            selectedDomains: selectedDomains,
                            isAddToExistingDomainList: true,
                            projectHandle: this.currentHandle
                        },
                        class: 'vertical-percent-100 horizontal-percent-100'
                    }).then((response) => {
                        if (response && response.modalData && response.modalData.domain) {
                            this.selectedDomainList = response.modalData.domain;
                            this.genericGrids.first.loadData();
                        }
                    });
                } else {
                    this.commonService.openModal((CreateEditEntityModalComponent), {
                        modalData: newDomainList
                    }).then((response) => {
                        if (response && response.modalData && response.modalData.domain) {
                            this.selectedDomainList = response.modalData.domain;
                            this.genericGrids.first.loadData();
                        }
                    });
                }
            }
        }
    }
    moveItemInList(arr: any, fromIndex: number, toIndex: number) {
        const element = arr[fromIndex];
        arr.splice(fromIndex, 1);
        arr.splice(toIndex, 0, element);
    }

    responseCallback = (response: any) => {
        if (response?.domains) {
            response.domains = response.domains.sort();
            const sysDomIdx = response.domains.findIndex(d => d.name === SYS_GENERATED_DOMAIN_LIST);
            if (sysDomIdx > 0) {
                this.moveItemInList(response.domains, sysDomIdx, 0);
            }
            this.selectedDomainList = this.selectedDomainList ? this.selectedDomainList : new DomainList(response.domains[0]);
            this.numOfDomainLists = response.domains.length;
            if (response.domains.length === 0) {
                this.genericGrids.last.setData([]);
            }
        }
    }

    loadDataFinishedCallback = () => {
        if (this.selectedDomainList && this.selectedDomainList.handle && this.genericGrids.first.gridApi) {
            this.genericGrids.first.gridApi.forEachNode((node) => {
                if (this.selectedDomainList.handle === node.data.handle) {
                    node.setSelected(true);
                    this.genericGrids.first.gridApi.ensureNodeVisible(node);
                }
            });
        } else {
            const domainRows = this.genericGrids.first.getRows();
            if (domainRows && domainRows.length > 0) {
                this.selectedDomainList = domainRows[0].data;
                this.genericGrids.first.selectNode(domainRows[0]);
            } else {
                this.listDomainEntries(this.selectedDomainList?.handle, this.selectedDomainList?.projectHandle);
            }
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data) {
            const domainList = new DomainList(rowNode.data);
            if (domainList) {
                this.editDomainList(domainList);
            }
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.genericGrids.first.gridApi) {
            this.genericGrids.last.gridCheckedItems = [];
            this.selectedDomainList = event.api.getSelectedRows()[0];
            this.selectedDomains = [];
            if (this.selectedDomainList && this.selectedDomainList.handle && this.selectedDomainList.projectHandle) {
                this.numberOfSelectedDomains = 0;
                this.listDomainEntries(this.selectedDomainList.handle, this.selectedDomainList.projectHandle);
            }
        }
    }

    onCustomFilterCallback = (event?: SelectionChangedEvent) => {
        if (this.selectedDomainList) {
            this.listDomainEntries(this.selectedDomainList.handle, this.selectedDomainList.projectHandle);
        }
    }

    listDomainEntries(domainHandle: string, requestHandle: string) {
        const currentDomainHandle = this.selectedDomainList ? this.selectedDomainList.handle : null;
        if (domainHandle && requestHandle) {
            this.isBusy = true;
            this.restService.post(Endpoint.projectManager_listDomainEntries, {
                domainHandle: domainHandle,
                requestHandle: requestHandle,
                systemScope: this.isSystemUser,
                maxEntries: DOMAINS_MAX_DISPLAY_GRID_ENTRIES,
                filters: this.filterQuery ? [{
                    attribute: 'name',
                    value: this.filterQuery,
                    operator: 'LIKE'
                }] : []
            }).then(res => {
                if (res && res.domainEntries && res.domainEntries.length > 0 && currentDomainHandle === this.selectedDomainList.handle) {
                    this.currentDomainEntries = res.domainEntries;
                    this.currentDomainEntries = this.currentDomainEntries.sort();
                    this.domains = [];
                    res.domainEntries.forEach((domain, index) => {
                        return this.domains[index] = {
                            domainName: domain
                        };
                    });

                    if (this.filterQuery !== '') {
                        this.onFilterApply(this.filterQuery);
                    } else {
                        this.genericGrids.last.setData(this.domains);
                    }
                    if (this.selectedDomains?.length > 0) {
                        this.selectedDomains.forEach(domain => this.genericGrids.last.findNode(n => n?.data?.domainName === domain.domainName, true));
                    }
                } else {
                    this.currentDomainEntries = [];
                    this.genericGrids.last.setData(this.currentDomainEntries);
                }
                this.totalNumberOfDomainEntries = res?.totalNumberOfEntries || 0;
                this.setDomainsGridEntriesCount(res?.domainEntries ? res.domainEntries.length : 0);
                this.isBusy = false;
            }, () => { this.isBusy = false; });
        }
    }

    onFilterApply(queryStr: string, filterType: FilterTemplateType = FilterTemplateType.freeform, filterColumnName: string = ''): void {
        this.filterQuery = queryStr;
        if (filterType === FilterTemplateType.clear && this.genericGrids?.last) {
            this.genericGrids.last.onFilterApply(queryStr, filterType, filterColumnName);
            this.listDomainEntries(this.selectedDomainList.handle, this.selectedDomainList.projectHandle);
        }

        if (filterType !== FilterTemplateType.clear) {
            if (filterColumnName === 'Domain Name' && this.totalNumberOfDomainEntries > DOMAINS_MAX_DISPLAY_GRID_ENTRIES && this.selectedDomainList) {
                this.needsBackendFiltering = true;
                this.listDomainEntries(this.selectedDomainList.handle, this.selectedDomainList.projectHandle);
            } else {
                const filtered = _.filter(this.domains, (field) => field.domainName ? field.domainName.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
                this.genericGrids.last.setData(filtered);
                this.setDomainsGridEntriesCount(filtered.length);
            }
        }
    }

    countSelectedDomains = (event?: SelectionChangedEvent) => {
        this.selectedDomains = [];
        if (event && event.api && this.genericGrids.last.gridApi) {
            const selectedRows = event.api.getSelectedRows();
            this.numberOfSelectedDomains = selectedRows.length;
            this.selectedDomains.push(...selectedRows);
        }
    }

    noDomainEntryIsSelected(): boolean {
        return this.numberOfSelectedDomains === 0;
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    deleteSelectedDomains(): void {
        if (this.genericGrids?.last?.gridApi) {
            const domainEntriesSelected = this.genericGrids.last.gridApi.getSelectedRows(),
                allDomainEntries = [];
            domainEntriesSelected.forEach((domain, index) => {
                allDomainEntries[index] = domain.domainName;
            });
            this.restService.post(Endpoint.projectManager_updateDomain, {
                description: this.selectedDomainList.description,
                domainEntries: allDomainEntries,
                handle: this.selectedDomainList.handle,
                name: this.selectedDomainList.name,
                updateOperation: 'REMOVE_ENTRIES',
                systemScope: this.isSystemUser
            }).then(response => {
                if (response.domain && response.domain.handle && response.domain.projectHandle) {
                    this.listDomainEntries(response.domain.handle, response.domain.projectHandle);
                }
            });
        }
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = projectHandle ? HelpIndex.myedisc_case_domains_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_domain_lists_dlg : HelpIndex.org_doman_lists_template_dlg);

        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        const permissionType = projectHandle ? ObjectActionListType.PRJ_DOMAIN_LISTS : this.isSystemUser ? ObjectActionListType.SYSTEM_DOMAIN_LISTS : ObjectActionListType.ORG_DOMAIN_LISTS;
        this.hasPermissions = Permissions.checkUserPermission(permissionType, ActionType.CREATE);

        if (projectHandle) {
            this.contextMenuOptions[1].permissions = [new PermissionSet({ permissionType: permissionType, permissionActions: [ActionType.DELETE] })];
        }
        if (this.currentHandle) {
            this.requestInfo.data = {
                projectHandle: this.currentHandle,
                systemScope: this.isSystemUser
            };
            if (this.genericGrids && this.genericGrids.first && this.genericGrids.last) {
                this.selectedDomainList = this.selectedDomainList ?? null;
                this.genericGrids.last.setData([]);
                this.genericGrids.first.setData([]);
                this.genericGrids.first.loadData();
                this.genericGrids.last.isDataFiltered = false;
                this.genericGrids.last.gridContext.clearAllFilters.emit();
            }
        }
    }

    setDomainsGridEntriesCount(numberOfDisplayedEntries: number) {
        this.domainGridEntriesCount = '1 - ' + numberOfDisplayedEntries + ' / ' + this.totalNumberOfDomainEntries;
    }

    isDisabled(): boolean { return false; }
}
